import * as R from 'ramda'
import { Offer, PriceEstimation } from '../../../../types/types'
import { isFutureProportionalPrice, isStartBeforeAcceptanceDates } from 'app/app/common/offer'
import { getFutureCalendarDates } from 'app/app/common/common-utils'
import { sumTotalDiscountedPrice } from 'app/app/common/prices'
import { getPriceEstimationBasePrice, validatePriceEstimation } from 'app/app/common/validation/price-estimation-validation'

const getProportionalPriceEstimation = (offer: Offer): ReadonlyArray<PriceEstimation> | undefined => {
  const {
    priceEstimation = [],
    isEscalationFlow: { active, escalatedAnnualPrice, escalationVasDiscountedPrices }
  } = offer
  const price = active
    ? escalatedAnnualPrice + escalationVasDiscountedPrices
    : offer.prices
      ? sumTotalDiscountedPrice(offer.prices)
      : 0
  if (isFutureProportionalPrice(offer) && priceEstimation.length === 0) {
    return getFutureCalendarDates(
      (offer.commercialTerms.VBEGDAT as string) || '',
      (offer.commercialTerms.VABNDAT as string) || '',
      'YYYY-MM-DD',
      'YYYY-MM-DD'
    ).map((d, i) => (i === 0 ? { ...d, price } : { ...d, price: undefined }))
  }
  // TODO: for only future price?
  return offer.priceEstimation
}

export const setOfferPriceEstimation = (offer: Offer) => ({
  ...offer,
  priceEstimation: getProportionalPriceEstimation(offer)
})

export const reconcileSingleYearPriceEstimation = (offer: Offer): Offer => {
  const { VBEGDAT, VABNDAT } = offer.commercialTerms || {}
  if (
    offer.finalized ||
    !isFutureProportionalPrice(offer) ||
    !isStartBeforeAcceptanceDates(VBEGDAT as string, VABNDAT as string)
  ) {
    return offer
  }

  const basePrice = getPriceEstimationBasePrice(offer)
  if (!Number.isFinite(basePrice) || basePrice <= 0) {
    return offer
  }

  const dates = getFutureCalendarDates(VBEGDAT as string, VABNDAT as string, 'YYYY-MM-DD', 'YYYY-MM-DD')
  if (dates.length !== 1) {
    return offer
  }

  const rows = offer.priceEstimation
  if (rows?.length) {
    if (!offer.isEscalationFlow?.active) {
      return offer
    }
    if (
      validatePriceEstimation(offer).isRight() &&
      rows[0].from === dates[0].from &&
      rows[rows.length - 1].to === dates[0].to
    ) {
      return offer
    }
  }

  return setOfferPriceEstimation({ ...offer, priceEstimation: [] })
}

export const resetOfferPriceEstimation = (offer: Offer) => ({
  ...offer,
  priceEstimation: resetBasePriceEstimation(offer)
})

export const resetEscalationFlowOfferPriceEstimation = (offer: Offer) => ({
  ...offer,
  priceEstimation: getProportionalPriceEstimation({ ...offer, priceEstimation: [] })
})

const resetBasePriceEstimation = ({ priceEstimation, prices, isEscalationFlow }: Offer) => {
  if (!priceEstimation) {
    return undefined
  }
  const { active, escalatedAnnualPrice, escalationVasDiscountedPrices } = isEscalationFlow
  const price = active
    ? escalatedAnnualPrice + escalationVasDiscountedPrices
    : prices
      ? sumTotalDiscountedPrice(prices)
      : 0
  return R.head(priceEstimation) ? R.assocPath([0, 'price'], price, priceEstimation) : priceEstimation
}
